import React from 'react';
import { DocsLayout } from '../components/DocsLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Shield } from 'lucide-react';

export const ServiceProvidersPage: React.FC = () => {
  return (
    <DocsLayout
      title='Service Providers'
      description='Companies that help us run Vehix'
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Legal', path: '/docs/legal' },
        { label: 'Service Providers' }
      ]}
    >
      <div className='max-w-[1000px]'>
        <p className='text-sm text-muted-foreground italic mb-8'>Last Updated: January 28, 2026</p>

        <div className='mb-8'>
          <Card className='border-blue-500/50 bg-blue-500/5'>
            <CardHeader>
              <div className='flex items-center gap-2'>
                <Building2 className='h-5 w-5 text-blue-600' />
                <CardTitle className='text-lg text-blue-700 dark:text-blue-400'>
                  Why We Share This Information
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className='text-sm leading-6'>
              <p>
                We believe you should know who helps us run Vehix and what data they can see. This
                page lists the companies we work with and explains what they do.
              </p>
            </CardContent>
          </Card>
        </div>

        <section className='mb-20' id='how-we-choose'>
          <h2 className='text-3xl font-bold mb-6'>1. How We Choose Partners</h2>
          <p className='text-sm leading-6 mb-4'>
            Before we work with any company, we make sure they:
          </p>
          <ul className='list-disc pl-6 space-y-2'>
            <li className='text-sm leading-6'>Have strong security practices</li>
            <li className='text-sm leading-6'>Will only use your data to provide their service</li>
            <li className='text-sm leading-6'>Have agreed to keep your data confidential</li>
            <li className='text-sm leading-6'>Meet our standards for data protection</li>
          </ul>
        </section>

        <section className='mb-20' id='service-providers'>
          <h2 className='text-3xl font-bold mb-6'>2. Our Service Providers</h2>

          <div className='space-y-6'>
            <Card className='border-border'>
              <CardHeader>
                <CardTitle className='text-lg'>Supabase</CardTitle>
              </CardHeader>
              <CardContent className='text-sm leading-6'>
                <p className='mb-4'>
                  <strong>What they do:</strong> Stores your data securely in the cloud and helps
                  manage user logins.
                </p>
                <p className='mb-4'>
                  <strong>What data they see:</strong> All data you store in Vehix (recall records,
                  technician info, route data).
                </p>
                <p className='mb-4'>
                  <strong>Location:</strong> United States
                </p>
                <p>
                  <strong>Why we use them:</strong> Supabase provides secure, reliable database
                  services that keep your data safe.
                </p>
              </CardContent>
            </Card>

            <Card className='border-border'>
              <CardHeader>
                <CardTitle className='text-lg'>Google</CardTitle>
              </CardHeader>
              <CardContent className='text-sm leading-6'>
                <p className='mb-4'>
                  <strong>What they do:</strong> Provides secure sign-in if you choose to log in
                  with your Google account.
                </p>
                <p className='mb-4'>
                  <strong>What data they see:</strong> Your email address and name (only if you use
                  Google sign-in).
                </p>
                <p className='mb-4'>
                  <strong>Location:</strong> United States
                </p>
                <p>
                  <strong>Why we use them:</strong> Google sign-in is secure and convenient for
                  users who already have Google accounts.
                </p>
              </CardContent>
            </Card>

            <Card className='border-border'>
              <CardHeader>
                <CardTitle className='text-lg'>OpenStreetMap</CardTitle>
              </CardHeader>
              <CardContent className='text-sm leading-6'>
                <p className='mb-4'>
                  <strong>What they do:</strong> Provides map data and helps us find locations
                  (geocoding).
                </p>
                <p className='mb-4'>
                  <strong>What data they see:</strong> Addresses when we need to find them on a map.
                </p>
                <p className='mb-4'>
                  <strong>Location:</strong> Various (community-operated)
                </p>
                <p>
                  <strong>Why we use them:</strong> OpenStreetMap is a reliable, open-source mapping
                  service that helps us show dealer locations on maps.
                </p>
              </CardContent>
            </Card>

            <Card className='border-border'>
              <CardHeader>
                <CardTitle className='text-lg'>Leaflet</CardTitle>
              </CardHeader>
              <CardContent className='text-sm leading-6'>
                <p className='mb-4'>
                  <strong>What they do:</strong> Displays interactive maps in the Vehix application.
                </p>
                <p className='mb-4'>
                  <strong>What data they see:</strong> Map coordinates and dealer locations you
                  view.
                </p>
                <p className='mb-4'>
                  <strong>Location:</strong> Open-source (no central server)
                </p>
                <p>
                  <strong>Why we use them:</strong> Leaflet is a trusted, widely-used library for
                  displaying maps in web applications.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className='mb-20' id='data-protection'>
          <h2 className='text-3xl font-bold mb-6'>3. How We Protect Your Data</h2>

          <div className='mb-6'>
            <Card className='border-green-500/50 bg-green-500/5'>
              <CardHeader>
                <div className='flex items-center gap-2'>
                  <Shield className='h-5 w-5 text-green-600' />
                  <CardTitle className='text-lg text-green-700 dark:text-green-400'>
                    Our Commitments
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className='text-sm leading-6'>
                <ul className='list-disc pl-6 space-y-2'>
                  <li>
                    <strong>Minimum access:</strong> Each provider only sees data they need
                  </li>
                  <li>
                    <strong>Contracts:</strong> All providers have signed agreements to protect your
                    data
                  </li>
                  <li>
                    <strong>No selling:</strong> None of these providers can sell your data
                  </li>
                  <li>
                    <strong>Regular review:</strong> We regularly check that providers meet our
                    standards
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className='mb-20' id='changes'>
          <h2 className='text-3xl font-bold mb-6'>4. Changes to This List</h2>
          <p className='text-sm leading-6'>
            We may add or remove service providers as needed to run Vehix. When we make changes, we
            will update this page. Major changes that significantly affect your data will be
            communicated directly.
          </p>
        </section>

        <section className='mb-20' id='questions'>
          <h2 className='text-3xl font-bold mb-6'>5. Questions?</h2>
          <p className='text-sm leading-6 mb-6'>
            If you have questions about our service providers:
          </p>

          <div className='p-6 bg-muted/30 rounded-lg border border-border'>
            <p className='text-sm leading-6'>
              <strong>Kebi AI LLC</strong>
              <br />
              Email: <span className='text-primary'>privacy@kebi.ai</span>
              <br />
              Support: <span className='text-primary'>support@kebi.ai</span>
            </p>
          </div>
        </section>
      </div>
    </DocsLayout>
  );
};
